import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTrackView } from "@/hooks/use-track-view";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { formatCurrency, formatShortDate, stageMeta, projectTypeLabels, leadSourceLabels, dollarsToCents, centsToDollars } from "@/lib/format";
import { useState, useMemo, useEffect } from "react";
import { Plus, Pencil, Trash2, ExternalLink, Download, Share2, Copy, Link2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DndContext, useDraggable, useDroppable, type DragEndEvent, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { LeadCapture } from "@/components/lead-capture";
import { DailyDrivers } from "@/components/daily-drivers";
import { LeadSourceBreakdown } from "@/components/lead-source-breakdown";
import { toast } from "sonner";
import { notifyDealClosed } from "@/lib/deal-alerts.functions";

type Stage = "lead" | "proposal_sent" | "follow_up" | "closed_won" | "closed_lost";
const STAGES: Stage[] = ["lead", "proposal_sent", "follow_up", "closed_won", "closed_lost"];

export const Route = createFileRoute("/_authenticated/pipeline")({
  head: () => ({ meta: [{ title: "Deal Tracker — CEO Boardroom" }] }),
  component: PipelinePage,
});

import { localTodayISO } from "@/lib/money";
function todayISO() { return localTodayISO(); }

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportProposalsCsv(rows: any[]) {
  if (!rows.length) return;
  const headers = [
    "Client",
    "Stage",
    "Value (USD)",
    "Project Type",
    "Lead Source",
    "Created",
    "Last Touch",
    "Next Action",
    "Closed Date",
    "Notes",
  ];
  const lines = [headers.join(",")];
  for (const p of rows) {
    lines.push([
      p.client_name ?? "",
      stageMeta[p.stage as Stage]?.label ?? p.stage ?? "",
      ((p.value_cents ?? 0) / 100).toFixed(2),
      projectTypeLabels[p.project_type as keyof typeof projectTypeLabels] ?? p.project_type ?? "",
      leadSourceLabels[p.lead_source as keyof typeof leadSourceLabels] ?? p.lead_source ?? "",
      p.created_at ? p.created_at.slice(0, 10) : "",
      p.last_touch_at ?? "",
      p.next_action_at ?? "",
      p.closed_date ?? "",
      p.notes ?? "",
    ].map(csvEscape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `deal-tracker-${todayISO()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length} deal${rows.length === 1 ? "" : "s"}`);
}

function PipelinePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  useTrackView("pipeline");
  const [dialog, setDialog] = useState<{ mode: "new" } | { mode: "edit"; proposal: any } | null>(null);
  const [closeWonModal, setCloseWonModal] = useState<{ id: string; prevStage: Stage; prevLeadSource: string | null } | null>(null);
  const [closeLostModal, setCloseLostModal] = useState<{ id: string; prevStage: Stage } | null>(null);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [tab, setTab] = useState<"deals" | "leads" | "activity">("deals");
  useEffect(() => {
    const saved = sessionStorage.getItem("pipeline-tab");
    if (saved === "deals" || saved === "leads" || saved === "activity") setTab(saved);
  }, []);


  const { data: proposals = [] } = useQuery({
    queryKey: ["proposals", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("proposals").select("*").eq("user_id", user!.id).order("position");
      return data ?? [];
    },
  });

  // Live updates: a client accepting on the proposal page moves the card instantly.
  useEffect(() => {
    if (!user) return;
    const prevStages = new Map<string, string>();
    const channel = supabase
      .channel(`deals-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "proposals", filter: `user_id=eq.${user.id}` },
        (payload: any) => {
          const row = payload.new;
          // payload.old lacks accepted_at (no REPLICA IDENTITY FULL), so compare
          // against the cached list from before this change instead.
          const cached = qc.getQueryData<any[]>(["proposals", user.id]);
          const before = cached?.find((d) => d.id === row?.id);
          const wasAccepted = !!before?.accepted_at || !cached;
          qc.invalidateQueries({ queryKey: ["proposals"] });
          if (payload.eventType === "UPDATE" && row?.accepted_at && !wasAccepted && !prevStages.has(row.id)) {
            prevStages.set(row.id, row.stage);
            toast.success(`${row.acceptor_name || row.client_name} accepted the proposal — deal closed!`);
          }
        },
      )
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "proposal_responses" }, () => {
        qc.invalidateQueries({ queryKey: ["proposal-responses"] });
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user, qc]);

  const today = todayISO();

  const analytics = useMemo(() => {
    const open = proposals.filter((p) => p.stage === "lead" || p.stage === "proposal_sent" || p.stage === "follow_up");
    const won = proposals.filter((p) => p.stage === "closed_won");
    const lost = proposals.filter((p) => p.stage === "closed_lost");
    const totalDecided = won.length + lost.length;
    const closeRate = totalDecided ? (won.length / totalDecided) * 100 : 0;
    const avgDeal = won.length ? Math.round(won.reduce((s, p) => s + p.value_cents, 0) / won.length) : 0;
    const overdue = open.filter((p: any) => p.next_action_at && p.next_action_at < today);
    const waiting = open.filter((p) => p.stage === "proposal_sent" || p.stage === "follow_up");
    return {
      pipelineValue: open.reduce((s, p) => s + p.value_cents, 0),
      closeRate, avgDeal,
      overdue: overdue.length,
      waitingOn: waiting.length,
      wins: won.length, losses: lost.length,
    };
  }, [proposals, today]);

  const detailProposal = detailsId ? proposals.find((p) => p.id === detailsId) : null;

  const move = useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: Stage; _prevStage?: Stage; _silent?: boolean }) => {
      const update: any = { stage, last_touch_at: today };
      if (stage === "closed_won" || stage === "closed_lost") update.closed_date = today;
      const { error } = await supabase.from("proposals").update(update).eq("id", id);
      if (error) throw error;
      if (stage === "closed_won") void notifyDealClosed({ data: { id } });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["proposals"] });
      if (vars._silent || !vars._prevStage || vars._prevStage === vars.stage) return;
      const prev = vars._prevStage;
      toast(`Moved to ${stageMeta[vars.stage].label}`, {
        action: {
          label: "Undo",
          onClick: () => {
            move.mutate({ id: vars.id, stage: prev, _silent: true });
          },
        },
      });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  function onDragEnd(e: DragEndEvent) {
    if (!e.over) return;
    const id = String(e.active.id);
    const newStage = String(e.over.id) as Stage;
    const p = proposals.find((x) => x.id === id);
    if (!p || p.stage === newStage) return;
    if (newStage === "closed_won") {
      if (!p.lead_source) { setCloseWonModal({ id, prevStage: p.stage, prevLeadSource: p.lead_source ?? null }); return; }
    }
    if (newStage === "closed_lost") { setCloseLostModal({ id, prevStage: p.stage }); return; }
    move.mutate({ id, stage: newStage, _prevStage: p.stage });
  }

  function openDetails(p: any) {
    setDetailsId(p.id);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold">Deal Tracker</h1>
          <p className="text-sm text-muted-foreground mt-1">Who am I waiting on? What's the next move?</p>
        </div>
        {tab === "deals" && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => exportProposalsCsv(proposals)}
              disabled={proposals.length === 0}
            >
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
            <Button onClick={() => setDialog({ mode: "new" })} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">
              <Plus className="h-4 w-4 mr-1.5" /> New Deal
            </Button>
          </div>
        )}
      </div>

      <div className="w-full border-b border-[color:var(--hairline)]" role="tablist" aria-label="Deal Tracker sections">
        <div className="grid grid-cols-3">
          {([
            { id: "deals", label: "Deals", hint: "Your pipeline board" },
            { id: "leads", label: "Leads", hint: "New relationships" },
            { id: "activity", label: "Activity", hint: "DMs, calls, content" },
          ] as const).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setTab(t.id);
                  sessionStorage.setItem("pipeline-tab", t.id);
                }}
                className="group relative px-2 pb-3 pt-1 text-center transition-colors"
              >
                <span className={`block text-sm font-semibold ${active ? "text-foreground" : "text-muted-foreground group-hover:text-foreground/80"}`}>
                  {t.label}
                </span>
                <span className={`block text-[11px] mt-0.5 ${active ? "text-muted-foreground" : "text-muted-foreground/60"}`}>
                  {t.hint}
                </span>
                <span
                  className={`absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[color:var(--gold)] transition-all duration-200 ${
                    active ? "opacity-100 scale-x-100" : "opacity-0 scale-x-50 group-hover:opacity-40 group-hover:scale-x-75"
                  }`}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      </div>

      {tab === "leads" && user && (
        <div className="space-y-6" role="tabpanel">
          <LeadCapture userId={user.id} />
          <LeadSourceBreakdown userId={user.id} />
        </div>
      )}

      {tab === "activity" && user && (
        <div className="space-y-6" role="tabpanel">
          <DailyDrivers userId={user.id} />
        </div>
      )}

      {tab === "deals" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Mini label="Pipeline Value" value={formatCurrency(analytics.pipelineValue)} />
            <Mini label="Waiting On Reply" value={String(analytics.waitingOn)} />
            <Mini label="Overdue Follow-ups" value={String(analytics.overdue)} tone={analytics.overdue > 0 ? "danger" : undefined} />
            <Mini label="Close Rate" value={`${analytics.closeRate.toFixed(0)}%`} sub={`${analytics.wins}W / ${analytics.losses}L`} />
          </div>

          {proposals.length === 0 ? (
            <div className="boardroom-card p-10 text-center">
              <p className="text-foreground/80 mb-2">No proposals yet.</p>
              <p className="text-sm text-muted-foreground">Every client started as a conversation. Add your first one.</p>
            </div>
          ) : (
            <DndContext sensors={sensors} onDragEnd={onDragEnd}>
              <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
                {STAGES.map((s) => (
                  <Column
                    key={s}
                    stage={s}
                    items={proposals.filter((p) => p.stage === s)}
                    onEdit={(p) => setDialog({ mode: "edit", proposal: p })}
                    onTap={isMobile ? openDetails : undefined}
                  />
                ))}
              </div>
            </DndContext>
          )}
        </>
      )}


      <ProposalDialog
        state={dialog}
        onClose={() => setDialog(null)}
        userId={user?.id}
      />
      <CloseWonDialog
        modal={closeWonModal}
        onDone={() => setCloseWonModal(null)}
        onSaved={(id, prevStage) => {
          toast("Moved to Closed Won", {
            action: {
              label: "Undo",
              onClick: () => move.mutate({ id, stage: prevStage, _silent: true }),
            },
          });
        }}
      />
      <CloseLostDialog
        modal={closeLostModal}
        onDone={() => setCloseLostModal(null)}
        onSaved={(id, prevStage) => {
          toast("Moved to Closed Lost", {
            action: {
              label: "Undo",
              onClick: () => move.mutate({ id, stage: prevStage, _silent: true }),
            },
          });
        }}
      />
      <DealDetailsSheet
        proposal={detailProposal}
        onClose={() => setDetailsId(null)}
        onEdit={(p) => { setDetailsId(null); setDialog({ mode: "edit", proposal: p }); }}
        onMove={(p, stage) => {
          if (stage === "closed_won" && !p.lead_source) {
            setDetailsId(null);
            setCloseWonModal({ id: p.id, prevStage: p.stage, prevLeadSource: null });
            return;
          }
          if (stage === "closed_lost") {
            setDetailsId(null);
            setCloseLostModal({ id: p.id, prevStage: p.stage });
            return;
          }
          move.mutate({ id: p.id, stage, _prevStage: p.stage });
          setDetailsId(null);
        }}
      />
    </div>
  );
}

function Mini({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "danger" }) {
  return (
    <div className="boardroom-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display text-xl font-semibold tabular mt-1 ${tone === "danger" ? "text-[color:var(--danger)]" : "text-[color:var(--gold)]"}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground tabular mt-1">{sub}</div>}
    </div>
  );
}

function Column({ stage, items, onEdit, onTap }: { stage: Stage; items: any[]; onEdit: (p: any) => void; onTap?: (p: any) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const meta = stageMeta[stage];
  const total = items.reduce((s, p) => s + p.value_cents, 0);
  return (
    <div ref={setNodeRef} className={`boardroom-card p-3 min-h-[400px] transition ${isOver ? "ring-1 ring-[color:var(--gold)]" : ""}`}>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-[11px] uppercase tracking-wider font-medium" style={{ color: meta.color }}>{meta.label}</div>
        <div className="text-[10px] text-muted-foreground tabular">{items.length}</div>
      </div>
      <div className="text-[10px] text-muted-foreground tabular mb-3 px-1">{formatCurrency(total)}</div>
      <div className="space-y-2">
        {items.map((p) => <Card key={p.id} p={p} onEdit={() => onEdit(p)} onTap={onTap ? () => onTap(p) : undefined} />)}
      </div>
    </div>
  );
}

function Card({ p, onEdit, onTap }: { p: any; onEdit: () => void; onTap?: () => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: p.id });
  const style = transform ? { transform: `translate3d(${transform.x}px,${transform.y}px,0)`, zIndex: 50 } : undefined;
  const today = new Date().toISOString().slice(0, 10);
  const overdue = p.next_action_at && p.next_action_at < today;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`boardroom-card p-3 select-none relative group ${isDragging ? "opacity-50" : ""} ${overdue ? "ring-1 ring-[color:var(--danger)]/60" : ""}`}
    >
      {/* Drag handle covers most of card */}
      <div
        {...listeners}
        {...attributes}
        onClick={(e) => {
          if (!onTap) return;
          // Ignore synthetic clicks from drag start
          if (isDragging) return;
          e.stopPropagation();
          onTap();
        }}
        className={`touch-none pr-8 ${onTap ? "cursor-pointer active:cursor-grabbing" : "cursor-grab active:cursor-grabbing"}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{p.client_name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{projectTypeLabels[p.project_type]}</div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2.5">
          <div className="text-sm tabular text-[color:var(--gold)]">{formatCurrency(p.value_cents)}</div>
          {p.last_touch_at && <div className="text-[10px] text-muted-foreground tabular">Touch {formatShortDate(p.last_touch_at)}</div>}
        </div>
        {p.next_action && (
          <div className={`mt-2 pt-2 border-t border-[color:var(--hairline)] text-[10px] ${overdue ? "text-[color:var(--danger)]" : "text-muted-foreground"}`}>
            → {p.next_action}
            {p.next_action_at && <span className="tabular ml-1">· {formatShortDate(p.next_action_at)}</span>}
          </div>
        )}
      </div>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        title="Edit deal"
        aria-label="Edit deal"
        className="absolute top-2 right-2 h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-[color:var(--gold)] hover:bg-[color:var(--surface-2)]/60 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 transition"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ---------- Mobile deal details sheet ---------- */

function DealDetailsSheet({
  proposal,
  onClose,
  onEdit,
  onMove,
}: {
  proposal: any | null;
  onClose: () => void;
  onEdit: (p: any) => void;
  onMove: (p: any, stage: Stage) => void;
}) {
  const open = !!proposal;
  const p = proposal;
  const today = todayISO();
  const overdue = p?.next_action_at && p.next_action_at < today;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="bottom" className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-h-[85vh] overflow-y-auto">
        {p && (
          <>
            <SheetHeader className="text-left">
              <div className="text-[10px] uppercase tracking-wider" style={{ color: stageMeta[p.stage as Stage].color }}>
                {stageMeta[p.stage as Stage].label}
              </div>
              <SheetTitle className="text-xl">{p.client_name}</SheetTitle>
              <div className="text-xs text-muted-foreground">{projectTypeLabels[p.project_type]}</div>
            </SheetHeader>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <InfoTile label="Value" value={formatCurrency(p.value_cents)} accent />
              <InfoTile label="First contact" value={p.sent_date ? formatShortDate(p.sent_date) : "—"} />
              <InfoTile label="Last touch" value={p.last_touch_at ? formatShortDate(p.last_touch_at) : "—"} />
              <InfoTile label="Lead source" value={p.lead_source ? leadSourceLabels[p.lead_source] ?? p.lead_source : "—"} />
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Next action</div>
              {p.next_action ? (
                <div className={`text-sm rounded-md border border-[color:var(--hairline)] p-3 ${overdue ? "text-[color:var(--danger)]" : "text-foreground/90"}`}>
                  → {p.next_action}
                  {p.next_action_at && (
                    <div className="text-[11px] mt-1 tabular text-muted-foreground">
                      By {formatShortDate(p.next_action_at)}{overdue ? " · Overdue" : ""}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground italic">No next action set.</div>
              )}
            </div>

            {p.notes && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Notes</div>
                <div className="text-sm whitespace-pre-wrap text-foreground/85 rounded-md border border-[color:var(--hairline)] p-3">{p.notes}</div>
              </div>
            )}

            {p.stage === "closed_lost" && p.lost_reason && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Lost reason</div>
                <div className="text-sm text-foreground/85 rounded-md border border-[color:var(--hairline)] p-3">{p.lost_reason}</div>
              </div>
            )}

            <ShareSection proposal={p} />

            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Move to</div>
              <div className="grid grid-cols-2 gap-2">
                {STAGES.filter((s) => s !== p.stage).map((s) => (
                  <button
                    key={s}
                    onClick={() => onMove(p, s)}
                    className="text-xs rounded-md border border-[color:var(--hairline)] px-3 py-2 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition text-left"
                  >
                    {stageMeta[s].label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
              <Button onClick={() => onEdit(p)} className="flex-1 bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">
                <Pencil className="h-4 w-4 mr-1.5" /> Edit deal
              </Button>
              <Button variant="ghost" onClick={onClose} className="text-muted-foreground">
                <ExternalLink className="h-4 w-4 mr-1.5 rotate-180" /> Close
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function InfoTile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-md border border-[color:var(--hairline)] p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm tabular mt-1 ${accent ? "text-[color:var(--gold)] font-medium" : ""}`}>{value}</div>
    </div>
  );
}

function ShareSection({ proposal }: { proposal: any }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(!!proposal?.share_enabled);
  const [slug, setSlug] = useState(proposal?.share_slug ?? "");

  // Keep the slug in sync if the proposal row refreshes (e.g. after it's generated).
  useEffect(() => {
    if (proposal?.share_slug && proposal.share_slug !== slug) setSlug(proposal.share_slug);
  }, [proposal?.share_slug]);
  const [toggling, setToggling] = useState(false);
  const [responses, setResponses] = useState<any[] | null>(null);

  const shareUrl = slug
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/p/${slug}`
    : "";

  useEffect(() => {
    if (!enabled) { setResponses(null); return; }
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("proposal_responses")
        .select("*")
        .eq("proposal_id", proposal.id)
        .order("created_at", { ascending: false });
      if (alive) setResponses(data ?? []);
    })();
    return () => { alive = false; };
  }, [proposal.id, enabled]);

  async function toggleShare() {
    setToggling(true);
    const next = !enabled;
    // Older deals may not have a slug yet — mint one when the page goes live.
    let nextSlug = slug;
    if (next && !nextSlug) {
      const bytes = new Uint8Array(9);
      crypto.getRandomValues(bytes);
      nextSlug = btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");
    }
    const { error } = await supabase
      .from("proposals")
      .update(nextSlug !== slug ? { share_enabled: next, share_slug: nextSlug } : { share_enabled: next })
      .eq("id", proposal.id);
    setToggling(false);
    if (error) { toast.error(error.message); return; }
    if (nextSlug !== slug) setSlug(nextSlug);
    setEnabled(next);
    toast.success(next ? "Proposal page is live" : "Proposal page disabled");
    qc.invalidateQueries({ queryKey: ["proposals"] });
    if (!next) setResponses(null);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — copy it manually");
    }
  }

  return (
    <div className="mt-4 rounded-md border border-[color:var(--hairline)] p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <Share2 className="h-3.5 w-3.5" /> Client proposal page
        </div>
        <button
          onClick={toggleShare}
          disabled={toggling}
          className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
            enabled
              ? "bg-[color:var(--success)]/15 text-[color:var(--success)] border-[color:var(--success)]/40"
              : "border-[color:var(--hairline)] text-muted-foreground hover:text-foreground"
          }`}
        >
          {enabled ? "Live" : "Off"}
        </button>
      </div>

      {enabled && shareUrl && (
        <>
          <div className="flex items-center gap-1.5 mt-2">
            <div className="flex-1 min-w-0 text-xs text-foreground/80 truncate font-mono bg-[color:var(--surface-2)] rounded px-2 py-1.5 border border-[color:var(--hairline)]">
              {shareUrl}
            </div>
            <button
              onClick={copyLink}
              className="shrink-0 text-xs inline-flex items-center gap-1 rounded-md border border-[color:var(--hairline)] px-2 py-1.5 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <a
            href={shareUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--gold-dim)] hover:text-[color:var(--gold)]"
          >
            <Link2 className="h-3.5 w-3.5" /> Open preview
          </a>

          {responses && responses.length > 0 && (
            <div className="mt-3 border-t border-[color:var(--hairline)] pt-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Responses ({responses.length})
              </div>
              <div className="space-y-2">
                {responses.map((r) => (
                  <div key={r.id} className="text-xs rounded-md border border-[color:var(--hairline)] p-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{r.prospect_name}</span>
                      <span className="text-[10px] text-muted-foreground">{formatShortDate(r.created_at)}</span>
                    </div>
                    <div className="text-[10px] text-[color:var(--gold-dim)] truncate">{r.prospect_email}</div>
                    {r.message && <p className="mt-1 text-foreground/80 whitespace-pre-wrap">{r.message}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {responses && responses.length === 0 && (
            <p className="text-[11px] text-muted-foreground mt-2">No responses yet. Share the link with {proposal.client_name}.</p>
          )}
        </>
      )}
      {!enabled && (
        <p className="text-[11px] text-muted-foreground mt-1.5">
          Turn on to generate a branded page your client can view and reply to — no login needed.
        </p>
      )}
    </div>
  );
}

/* ---------- Unified create / edit dialog ---------- */

type DialogState = { mode: "new" } | { mode: "edit"; proposal: any } | null;

function ProposalDialog({ state, onClose, userId }: { state: DialogState; onClose: () => void; userId?: string }) {
  const qc = useQueryClient();
  const isEdit = state?.mode === "edit";
  const existing = isEdit ? state.proposal : null;

  const [clientName, setClientName] = useState("");
  const [projectType, setProjectType] = useState("other");
  const [value, setValue] = useState("");
  const [sentDate, setSentDate] = useState(todayISO());
  const [leadSource, setLeadSource] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [nextActionAt, setNextActionAt] = useState("");
  const [stage, setStage] = useState<Stage>("lead");
  const [lostReason, setLostReason] = useState("");
  const [excludeFromRevenue, setExcludeFromRevenue] = useState(false);

  useEffect(() => {
    if (!state) return;
    if (state.mode === "edit") {
      const p = state.proposal;
      setClientName(p.client_name ?? "");
      setProjectType(p.project_type ?? "other");
      setValue(String(centsToDollars(p.value_cents ?? 0)));
      setSentDate(p.sent_date ?? todayISO());
      setLeadSource(p.lead_source ?? null);
      setNotes(p.notes ?? "");
      setNextAction(p.next_action ?? "");
      setNextActionAt(p.next_action_at ?? "");
      setStage(p.stage ?? "lead");
      setLostReason(p.lost_reason ?? "");
      setExcludeFromRevenue(!!p.exclude_from_revenue);
    } else {
      setClientName(""); setProjectType("other"); setValue("");
      setSentDate(todayISO()); setLeadSource(null); setNotes("");
      setNextAction(""); setNextActionAt(""); setStage("lead"); setLostReason("");
      setExcludeFromRevenue(false);
    }
  }, [state]);

  async function save() {
    if (!userId || !clientName.trim()) {
      toast.error("Client name is required");
      return;
    }
    const today = todayISO();
    const payload: any = {
      client_name: clientName.trim(),
      project_type: projectType,
      value_cents: dollarsToCents(value || "0"),
      sent_date: sentDate || today,
      lead_source: leadSource,
      notes: notes.trim() || null,
      next_action: nextAction.trim() || null,
      next_action_at: nextActionAt || null,
      stage,
      lost_reason: stage === "closed_lost" ? (lostReason.trim() || null) : null,
      exclude_from_revenue: excludeFromRevenue,
    };

    if (isEdit && existing) {
      const wasClosed = existing.stage === "closed_won" || existing.stage === "closed_lost";
      const nowClosed = stage === "closed_won" || stage === "closed_lost";
      if (!wasClosed && nowClosed) payload.closed_date = today;
      if (wasClosed && !nowClosed) payload.closed_date = null;
      payload.last_touch_at = today;
      const { error } = await supabase.from("proposals").update(payload).eq("id", existing.id);
      if (error) return toast.error(error.message);
      if (stage === "closed_won" && existing.stage !== "closed_won") void notifyDealClosed({ data: { id: existing.id } });
      toast.success("Deal updated");
    } else {
      payload.user_id = userId;
      payload.last_touch_at = today;
      const { error } = await supabase.from("proposals").insert(payload);
      if (error) return toast.error(error.message);
      toast.success("Deal added");
    }
    qc.invalidateQueries({ queryKey: ["proposals"] });
    qc.invalidateQueries({ queryKey: ["proposals-recent"] });
    onClose();
  }

  async function remove() {
    if (!isEdit || !existing) return;
    if (!confirm("Delete this deal? This can't be undone.")) return;
    const { error } = await supabase.from("proposals").delete().eq("id", existing.id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["proposals"] });
    qc.invalidateQueries({ queryKey: ["proposals-recent"] });
    toast.success("Deal removed");
    onClose();
  }

  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Deal" : "New Deal"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Client / company name"><Input value={clientName} onChange={(e) => setClientName(e.target.value)} autoFocus /></Field>
          <Field label="Project type">
            <Select value={projectType} onValueChange={setProjectType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(projectTypeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Value">
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input value={value} onChange={(e) => setValue(e.target.value)} type="number" className="pl-7 tabular" /></div>
            </Field>
            <Field label="First contact"><Input value={sentDate} onChange={(e) => setSentDate(e.target.value)} type="date" /></Field>
          </div>
          <Field label="Stage">
            <Select value={stage} onValueChange={(v) => setStage(v as Stage)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => <SelectItem key={s} value={s}>{stageMeta[s].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Lead source">
            <Select value={leadSource ?? ""} onValueChange={(v) => setLeadSource(v || null)}>
              <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>{Object.entries(leadSourceLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <Field label="Next action"><Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="e.g. Send follow-up email" /></Field>
            <Field label="By"><Input type="date" value={nextActionAt} onChange={(e) => setNextActionAt(e.target.value)} /></Field>
          </div>
          {stage === "closed_lost" && (
            <Field label="Lost reason"><Textarea value={lostReason} onChange={(e) => setLostReason(e.target.value)} rows={2} placeholder="Why didn't it close?" /></Field>
          )}
          <Field label="Notes (optional)"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></Field>

          <label className="flex items-start gap-2 rounded-md border border-[color:var(--hairline)] p-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeFromRevenue}
              onChange={(e) => setExcludeFromRevenue(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[color:var(--gold)]"
            />
            <span className="text-sm">
              <span className="font-medium">Exclude from revenue totals</span>
              <span className="block text-[11px] text-muted-foreground">
                Use this when the money is already logged elsewhere (e.g. one-off payments, payment-plan installments, or a manual monthly entry) to avoid double-counting.
              </span>
            </span>
          </label>


          <div className="flex items-center justify-between gap-2 pt-1">
            {isEdit ? (
              <Button variant="ghost" onClick={remove} className="text-[color:var(--danger)] hover:text-[color:var(--danger)]">
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete
              </Button>
            ) : <span />}
            <Button onClick={save} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">
              {isEdit ? "Save changes" : "Add Deal"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><Label className="text-xs mb-1.5 block">{label}</Label>{children}</div>;
}

function CloseWonDialog({
  modal,
  onDone,
  onSaved,
}: {
  modal: { id: string; prevStage: Stage; prevLeadSource: string | null } | null;
  onDone: () => void;
  onSaved: (id: string, prevStage: Stage) => void;
}) {
  const qc = useQueryClient();
  const [source, setSource] = useState("referral");
  async function save() {
    if (!modal) return;
    const { error } = await supabase.from("proposals").update({
      stage: "closed_won", lead_source: source as any, closed_date: todayISO(),
    }).eq("id", modal.id);
    if (error) return toast.error(error.message);
    void notifyDealClosed({ data: { id: modal.id } });
    qc.invalidateQueries({ queryKey: ["proposals"] });
    onSaved(modal.id, modal.prevStage);
    onDone();
  }
  return (
    <Dialog open={!!modal} onOpenChange={(v) => !v && onDone()}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-w-sm">
        <DialogHeader><DialogTitle>Where did this client come from?</DialogTitle></DialogHeader>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(leadSourceLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={save} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">Close Won</Button>
      </DialogContent>
    </Dialog>
  );
}

function CloseLostDialog({
  modal,
  onDone,
  onSaved,
}: {
  modal: { id: string; prevStage: Stage } | null;
  onDone: () => void;
  onSaved: (id: string, prevStage: Stage) => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  async function save() {
    if (!modal) return;
    const { error } = await supabase.from("proposals").update({
      stage: "closed_lost", lost_reason: reason.trim() || null, closed_date: todayISO(),
    }).eq("id", modal.id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["proposals"] });
    onSaved(modal.id, modal.prevStage);
    onDone(); setReason("");
  }
  return (
    <Dialog open={!!modal} onOpenChange={(v) => !v && onDone()}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-w-sm">
        <DialogHeader><DialogTitle>Why did this deal not close?</DialogTitle></DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Be honest. This is data for next time." />
        <Button onClick={save} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">Save</Button>
      </DialogContent>
    </Dialog>
  );
}
